import { DicomImageSlice } from '../dicom/dicomImageSlice';
import { Modality } from '../dicom/modality';
import { PatientDataSet } from '../dicom/patientdataset';
import { Series } from '../dicom/series';
import { ChangeTracker } from '../changes/ChangeTracker';
import {
    PatientSnapshot,
    SyncStatus,
    TrackableObject,
} from '../changes/DomainChange';
import { newPatientId, PatientId } from '../changes/ids';
import { DoseMatrix } from './doseMatrix';
import { Image3D } from './image3d';
import { Plan } from './plan';
import { ApprovableRegistration } from './approvableRegistration';
import { DeformableRegistration } from './deformableRegistration';
import { RigidRegistration } from './rigidRegistration';
import { StructureSet } from './structureset';
import { domainTrace } from '../diagnostics/domainTrace';

/**
 * Domain object for a Patient. This class captures all the data related to a patient.
 */

export type ImageRegistration = {
    image: Image3D;
    registrations: RigidRegistration[] | undefined;
};

export type ImageRegistrationsFor<T> = {
    image: Image3D;
    registrations: T[];
};

export type ImageApprovableRegistration =
    ImageRegistrationsFor<ApprovableRegistration>;

export class Patient implements TrackableObject {
    /**
     * The DICOM data set for the patient
     */
    public dataSet: PatientDataSet;

    readonly trackableKind = 'patient' as const;

    /**
     * Internal id\
     * Unique runtime identifier that is generated when the {@link Patient} is constructed.
     */
    public readonly id: PatientId;

    private tracker: ChangeTracker | null = null;
    private _syncStatus: SyncStatus = 'downloaded';

    private readonly _images: Image3D[] = new Array<Image3D>();

    private readonly _doses: DoseMatrix[] = new Array<DoseMatrix>();

    private readonly _structureSets: StructureSet[] = new Array<StructureSet>();

    private readonly _plans: Plan[] = new Array<Plan>();

    private readonly _registrations: RigidRegistration[] =
        new Array<RigidRegistration>();

    private readonly _deformableRegistrations: DeformableRegistration[] =
        new Array<DeformableRegistration>();

    get trackableId(): PatientId {
        return this.id;
    }

    get syncStatus(): SyncStatus {
        return this._syncStatus;
    }

    constructor(data: PatientDataSet, id: PatientId = newPatientId()) {
        this.dataSet = data;
        this.id = id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
        for (const ss of this._structureSets) {
            ss.attachTracker(tracker);
        }
        for (const reg of this.approvableRegistrations) {
            reg.attachTracker(tracker);
        }
        for (const dose of this._doses) {
            dose.attachTracker(tracker);
        }
        for (const plan of this._plans) {
            plan.attachTracker(tracker);
        }
        for (const image of this._images) {
            image.attachTracker(tracker);
        }
    }

    /**
     * Whether a change tracker is attached. `attachTracker` is permanent
     * (session dispose does not detach it), so a tracked aggregate must only
     * be mutated inside `tracker.command()`/`batch()` — a direct mutation
     * outside one throws. Callers without a command context (e.g. a
     * session-less teardown path) use this to avoid that throw.
     */
    public get isTracked(): boolean {
        return this.tracker !== null;
    }

    captureSnapshot(): PatientSnapshot {
        return {
            id: this.id,
            dicomPatientId: this.patientId,
            dicomPatientName:
                typeof this.dataSet?.PatientName === 'string'
                    ? this.dataSet.PatientName
                    : null,
            structureSetCount: this._structureSets.length,
            syncStatus: this._syncStatus,
        };
    }

    getStructureSetById(id: string): StructureSet | undefined {
        return this._structureSets.find((s) => s.id === id);
    }

    public get patientId(): string {
        return this.dataSet.PatientID;
    }

    public addImage(image: Image3D) {
        domainTrace.patient('addImage', {
            imageUid: image.dicomUid,
            totalImages: this._images.length + 1,
        });
        this._images.push(image);
        if (this.tracker) {
            image.attachTracker(this.tracker);
            this.tracker.markChanged(image, 'created');
            this.tracker.markChanged(this);
        }
    }

    public get images(): ReadonlyArray<Image3D> {
        return this._images;
    }

    public getImage3DWithDicomUid(uid: string): Image3D | undefined {
        return this._images.find((value: Image3D): boolean => {
            return value.dicomUid === uid;
        });
    }

    public getImageSliceWithSopInstanceUid(
        uid: string
    ): DicomImageSlice | undefined {
        for (const image of this._images) {
            const slice = image.imageSlices.find(
                (value: DicomImageSlice): boolean => {
                    return value.sopInstanceUid === uid;
                }
            );

            if (slice) {
                return slice;
            }
        }
        return undefined;
    }

    public addDose(dose: DoseMatrix) {
        domainTrace.patient('addDose', {
            doseUid: dose.sopInstanceUid,
            totalDoses: this._doses.length + 1,
        });
        this._doses.push(dose);
        if (this.tracker) {
            dose.attachTracker(this.tracker);
            this.tracker.markChanged(dose, 'created');
            this.tracker.markChanged(this);
        }
    }

    public get doses(): ReadonlyArray<DoseMatrix> {
        return this._doses;
    }

    public getDoseWithSopInstanceUid(uid: string): DoseMatrix | undefined {
        return this._doses.find((value: DoseMatrix): boolean => {
            return value.sopInstanceUid === uid;
        });
    }

    public removeDose(dose: DoseMatrix): ReadonlyArray<DoseMatrix> {
        domainTrace.patient('removeDose', {
            doseUid: dose.sopInstanceUid,
            totalDoses:
                this._doses.length - (this._doses.includes(dose) ? 1 : 0),
        });
        const index = this._doses.indexOf(dose);
        if (index > -1) {
            this._doses.splice(index, 1);
        }

        // Cascade: a plan must not keep pointing at a dose that is no
        // longer part of the aggregate. The plan's link setter is silent
        // by design, so mark it here — this method already runs inside a
        // command() when the patient is tracked.
        for (const plan of this._plans) {
            if (plan.doseMatrix === dose) {
                plan.linkDoseMatrix(null);
                this.tracker?.markChanged(plan);
            }
        }

        if (index > -1 && this.tracker) {
            this.tracker.markChanged(dose, 'deleted');
            this.tracker.markChanged(this);
        }

        return this.doses;
    }

    public addStructureSet(structureSet: StructureSet): void {
        domainTrace.patient('addStructureSet', {
            ssId: structureSet.id,
            totalStructureSets: this._structureSets.length + 1,
        });
        this._structureSets.push(structureSet);
        if (this.tracker) {
            structureSet.attachTracker(this.tracker);
            this.tracker.markChanged(structureSet, 'created');
            this.tracker.markChanged(this);
        }
    }

    public get structureSets(): ReadonlyArray<StructureSet> {
        return this._structureSets;
    }

    public addPlan(plan: Plan) {
        domainTrace.patient('addPlan', {
            planUid: plan.sopInstanceUid,
            totalPlans: this._plans.length + 1,
        });
        this._plans.push(plan);
        if (this.tracker) {
            plan.attachTracker(this.tracker);
            this.tracker.markChanged(plan, 'created');
            this.tracker.markChanged(this);
        }
    }

    public get plans(): ReadonlyArray<Plan> {
        return this._plans;
    }

    public getPlanWithSopInstanceUid(uid: string): Plan | undefined {
        return this._plans.find((plan) => {
            return plan.sopInstanceUid === uid;
        });
    }

    public addDeformableRegistration(registration: DeformableRegistration) {
        domainTrace.patient('addDeformableRegistration', {
            totalRegistrations: this._deformableRegistrations.length + 1,
        });
        this._deformableRegistrations.push(registration);
        if (this.tracker) {
            registration.attachTracker(this.tracker);
            this.tracker.markChanged(registration, 'created');
            this.tracker.markChanged(this);
        }
    }

    public addRegistration(registration: RigidRegistration) {
        domainTrace.patient('addRegistration', {
            regUid: registration.sopInstanceUid,
            totalRegistrations: this._registrations.length + 1,
        });
        this._registrations.push(registration);
        if (this.tracker) {
            registration.attachTracker(this.tracker);
            this.tracker.markChanged(registration, 'created');
            this.tracker.markChanged(this);
        }
    }

    public removeImage(image: Image3D): ReadonlyArray<Image3D> {
        domainTrace.patient('removeImage', {
            imageUid: image.dicomUid,
            totalImages:
                this._images.length - (this._images.includes(image) ? 1 : 0),
        });
        const index = this._images.indexOf(image);
        if (index > -1) {
            this._images.splice(index, 1);
        }

        // Cascade: a structure set must not keep pointing at an image
        // that is no longer part of the aggregate.
        for (const ss of this._structureSets) {
            if (ss.image === image) {
                ss.image = null;
                this.tracker?.markChanged(ss);
            }
        }

        if (index > -1 && this.tracker) {
            this.tracker.markChanged(image, 'deleted');
            this.tracker.markChanged(this);
        }

        return this.images;
    }

    public removeStructureSet(
        structureSet: StructureSet
    ): ReadonlyArray<StructureSet> {
        domainTrace.patient('removeStructureSet', {
            ssId: structureSet.id,
            totalStructureSets:
                this._structureSets.length -
                (this._structureSets.includes(structureSet) ? 1 : 0),
        });
        const index = this._structureSets.indexOf(structureSet);
        if (index > -1) {
            this._structureSets.splice(index, 1);
        }

        // Cascade: drop dangling structure-set references. Plans first —
        // a dose delegates `structureSet` to its plan when one is linked,
        // so unlinking the plan already clears the dose's view of it and
        // the dose branch below then only handles plan-less doses. Track the
        // plans we unlink so the dose loop can also mark plan-shadowed doses:
        // their resolved structure set just flipped to null even though they
        // hold no direct reference for detachStructureSet to clear.
        const unlinkedPlans = new Set<Plan>();
        for (const plan of this._plans) {
            if (plan.structureSet === structureSet) {
                plan.linkStructureSet(null);
                this.tracker?.markChanged(plan);
                unlinkedPlans.add(plan);
            }
        }
        for (const dose of this._doses) {
            // A dose can reference the removed set two ways: directly (its
            // own `_structureSet`, which detachStructureSet clears — note the
            // getter delegates to the plan when one is linked and would hide
            // this direct reference), or indirectly through a plan whose link
            // we just cleared above (plan-shadowed — detachStructureSet cannot
            // see it, since `_structureSet` was never set). Mark on either so
            // the dose's snapshot (structureSetId now null) rides a changeset
            // entry rather than only being reflected on the plan's.
            const detachedDirect = dose.detachStructureSet(structureSet);
            const shadowedByPlan =
                dose.plan !== null && unlinkedPlans.has(dose.plan);
            if (detachedDirect || shadowedByPlan) {
                this.tracker?.markChanged(dose);
            }
        }

        if (this.tracker) {
            this.tracker.markChanged(structureSet, 'deleted');
            this.tracker.markChanged(this);
        }

        return this.structureSets;
    }

    public removeRegistration(
        registration: ApprovableRegistration
    ): ReadonlyArray<ApprovableRegistration> {
        const rigid: ApprovableRegistration[] = this._registrations;
        const list = rigid.includes(registration)
            ? rigid
            : this._deformableRegistrations;
        const index = list.indexOf(registration);
        domainTrace.patient('removeRegistration', {
            regUid: registration.sopInstanceUid,
            totalRegistrations:
                this.approvableRegistrations.length - (index > -1 ? 1 : 0),
        });
        if (index > -1) {
            list.splice(index, 1);
            if (this.tracker) {
                this.tracker.markChanged(registration, 'deleted');
                this.tracker.markChanged(this);
            }
        }
        return this.approvableRegistrations;
    }

    public get deformableRegistrations(): ReadonlyArray<DeformableRegistration> {
        return this._deformableRegistrations;
    }

    public get approvableRegistrations(): ReadonlyArray<ApprovableRegistration> {
        return [...this._registrations, ...this._deformableRegistrations];
    }

    public get registrations(): ReadonlyArray<RigidRegistration> {
        return this._registrations;
    }

    /**
     * Structure sets drawn on the given image series: bound to an image of
     * that series, or naming it as their RT Referenced Series, so a set
     * whose image is not bound is still found. An empty UID matches
     * nothing, here and in the two Frame of Reference lookups below: an
     * unknown identifier is not evidence of a shared one.
     */
    public getStructureSetsForSeries(seriesUid: string): StructureSet[] {
        if (!seriesUid) {
            return [];
        }
        return this._structureSets.filter(
            (ss) =>
                ss.image?.seriesUid === seriesUid ||
                ss.dicomStructureSet.referencedSeriesUid === seriesUid
        );
    }

    /**
     * Returns structure sets that reference the given image.
     * Used to warn the user before deleting an image.
     */
    public getAffectedStructureSets(image: Image3D): StructureSet[] {
        return this._structureSets.filter((ss) => ss.image === image);
    }

    public getRegistrationWithSopInstanceUid(
        uid: string
    ): ApprovableRegistration | undefined {
        return this.approvableRegistrations.find(
            (reg) => reg.sopInstanceUid === uid
        );
    }

    /**
     * Returns the rigid registrations that link two images by matching
     * their Frames of Reference UIDs in either direction.
     *
     * Returns an empty array when both images share the same Frame of
     * Reference (no registration needed) or when either FOR UID is
     * missing.
     *
     * @param source - The first image
     * @param target - The second image
     */
    public findRegistrationsBetween(
        source: Image3D,
        target: Image3D
    ): RigidRegistration[] {
        return registrationsBetween(this._registrations, source, target);
    }

    public findCounterpartImage(
        registration: FrameOfReferencePair,
        image: Image3D
    ): Image3D | undefined {
        const imageFor = image.frameOfReferenceUid;
        if (!imageFor) {
            return undefined;
        }
        let counterpartFor: string | undefined;
        if (registration.sourceFrameOfReferenceUid === imageFor) {
            counterpartFor = registration.targetFrameOfReferenceUid;
        } else if (registration.targetFrameOfReferenceUid === imageFor) {
            counterpartFor = registration.sourceFrameOfReferenceUid;
        }
        if (!counterpartFor || counterpartFor === imageFor) {
            return undefined;
        }
        return this._images.find(
            (candidate) =>
                candidate.frameOfReferenceUid === counterpartFor &&
                !candidate.isSpatiallyResampled
        );
    }

    public findApprovableRegistrationsBetween(
        source: Image3D,
        target: Image3D
    ): ApprovableRegistration[] {
        return registrationsBetween(
            this.approvableRegistrations,
            source,
            target
        );
    }

    /**
     * Rigid registrations that link the given Frame of Reference, as either
     * endpoint. An empty UID matches nothing.
     */
    public getRegistrationsForFrameOfReference(
        frameOfReferenceUid: string
    ): RigidRegistration[] {
        if (!frameOfReferenceUid) {
            return [];
        }
        return this._registrations.filter(
            (reg) =>
                reg.sourceFrameOfReferenceUid === frameOfReferenceUid ||
                reg.targetFrameOfReferenceUid === frameOfReferenceUid
        );
    }

    /**
     * Doses computed in the given Frame of Reference. An empty UID matches
     * nothing.
     */
    public getDosesForFrameOfReference(
        frameOfReferenceUid: string
    ): DoseMatrix[] {
        if (!frameOfReferenceUid) {
            return [];
        }
        return this._doses.filter(
            (dose) => dose.dicomDose.frameOfReferenceUid === frameOfReferenceUid
        );
    }

    /**
     * Returns the rigid registrations a pair displayed on `primary` is
     * fused through: those whose source frame is the primary's and whose
     * target frame is the secondary's. A registration pointing the other
     * way renders the pair too, through the inverse matrix, but a manual
     * alignment made on the primary would then edit it backwards, so it is
     * not offered from that side. Empty for a shared or missing frame.
     */
    public findRegistrationsFrom(
        primary: Image3D,
        secondary: Image3D
    ): RigidRegistration[] {
        const primaryFor = primary.frameOfReferenceUid;
        const secondaryFor = secondary.frameOfReferenceUid;
        if (!primaryFor || !secondaryFor || primaryFor === secondaryFor) {
            return [];
        }
        return this._registrations.filter(
            (reg) =>
                reg.sourceFrameOfReferenceUid === primaryFor &&
                reg.targetFrameOfReferenceUid === secondaryFor
        );
    }

    public getAllApprovableRegistrationsForPrimary(
        primaryImage: Image3D
    ): ImageApprovableRegistration[] {
        return this.groupBySecondaryImage(primaryImage, (secondary) =>
            this.findApprovableRegistrationsBetween(primaryImage, secondary)
        );
    }

    private groupBySecondaryImage<T>(
        primaryImage: Image3D,
        registrationsFor: (secondary: Image3D) => T[]
    ): ImageRegistrationsFor<T>[] {
        const seen = new Set<string>();
        const result: ImageRegistrationsFor<T>[] = [];
        for (const image of this._images) {
            const seriesUid = image.seriesUid;
            if (
                image === primaryImage ||
                seriesUid === primaryImage.seriesUid ||
                seen.has(seriesUid)
            ) {
                continue;
            }
            seen.add(seriesUid);
            result.push({ image, registrations: registrationsFor(image) });
        }
        return result;
    }

    /**
     * Gets all unique image registrations for a primary image,
     * excluding the primary image itself and deduplicating by series UID.
     *
     * @param primaryImage - The primary image to get registrations for
     * @returns Array of unique ImageRegistration objects, deduplicated by series UID
     */
    public getAllRegistrationsForPrimary(
        primaryImage: Image3D
    ): ImageRegistration[] {
        return this.groupBySecondaryImage(primaryImage, (secondary) =>
            this.findRegistrationsFrom(primaryImage, secondary)
        );
    }

    /**
     * Gets priority registrations - those that have valid registration data.
     * Excludes the primary image and deduplicates by series UID.
     *
     * @param primaryImage - The primary image to filter against
     * @returns Array of ImageRegistration objects that have valid registration data
     */
    public getPriorityRegistrations(
        primaryImage: Image3D
    ): ImageRegistration[] {
        const allRegistrations =
            this.getAllRegistrationsForPrimary(primaryImage);

        return allRegistrations.filter((imgReg) => {
            // Must have registrations and not be the primary image
            return imgReg.registrations && imgReg.registrations.length > 0;
        });
    }

    /**
     * Gets completed registrations that are not in the priority list.
     * These are registrations that have registration data but are not
     * among the priority registrations.
     *
     * @param primaryImage - The primary image to filter against
     * @param priorityRegistrations - Array of priority registrations to exclude
     * @returns Array of ImageRegistration objects that are completed but not priority
     */
    public getCompletedRegistrations(
        primaryImage: Image3D,
        priorityRegistrations: ImageRegistration[]
    ): ImageRegistration[] {
        const allRegistrations =
            this.getAllRegistrationsForPrimary(primaryImage);

        // Create a set of priority series UIDs for efficient lookup
        const prioritySeriesUIDs = new Set(
            priorityRegistrations.map((img) => img.image.seriesUid)
        );

        return allRegistrations.filter((imgReg) => {
            // Must have registrations, not be the primary image, and not be in priority list
            return (
                imgReg.registrations &&
                imgReg.registrations.length > 0 &&
                !prioritySeriesUIDs.has(imgReg.image.seriesUid)
            );
        });
    }

    public getStructureSetWithSopInstanceUid(
        uid: string
    ): StructureSet | undefined {
        return this._structureSets.find((value: StructureSet): boolean =>
            value.hasSopInstanceUid(uid)
        );
    }

    /**
     * Retrieves a domain series item (ImageSlice, DoseMatrix, StructureSet, RigidRegistration, or Plan)
     * from the given series based on the SOP Instance UID.
     *
     * @param series - The series to search within
     * @param sopInstanceUid - The SOP Instance UID to search for
     * @returns The matching series item if found, otherwise undefined
     * @throws {Error} If the series modality is not supported
     */
    public getSeriesItemWithSopInstanceUid(
        series: Series,
        sopInstanceUid: string
    ):
        | DicomImageSlice
        | DoseMatrix
        | StructureSet
        | ApprovableRegistration
        | Plan
        | undefined {
        if (!series) {
            return undefined;
        }

        const modality = series.seriesModule.modality;

        switch (modality) {
            case Modality.CT:
            case Modality.MR:
            case Modality.PT:
                return this.getImageSliceWithSopInstanceUid(sopInstanceUid);
            case Modality.RTDOSE:
                return this.getDoseWithSopInstanceUid(sopInstanceUid);
            case Modality.RTSTRUCT:
                return this.getStructureSetWithSopInstanceUid(sopInstanceUid);
            case Modality.RTPLAN:
                return this.getPlanWithSopInstanceUid(sopInstanceUid);
            case Modality.REG:
                return this.getRegistrationWithSopInstanceUid(sopInstanceUid);
            default:
                throw new Error(`Unsupported modality: ${modality}`);
        }
    }
}

export interface FrameOfReferencePair {
    readonly sourceFrameOfReferenceUid: string;
    readonly targetFrameOfReferenceUid: string;
}

function registrationsBetween<T extends ApprovableRegistration>(
    list: ReadonlyArray<T>,
    source: Image3D,
    target: Image3D
): T[] {
    const srcFor = source.frameOfReferenceUid;
    const tgtFor = target.frameOfReferenceUid;
    if (!srcFor || !tgtFor || srcFor === tgtFor) {
        return [];
    }
    return list.filter(
        (reg) =>
            (reg.sourceFrameOfReferenceUid === srcFor &&
                reg.targetFrameOfReferenceUid === tgtFor) ||
            (reg.sourceFrameOfReferenceUid === tgtFor &&
                reg.targetFrameOfReferenceUid === srcFor)
    );
}
